/**
 * Persisted `useState` — reads once from `localStorage` on mount, writes on every change.
 * Storage failures (private browsing, quota) degrade to in-memory state rather than throwing.
 */

import { useCallback, useState } from "react";

import { logger } from "@/lib/logger";

export function useLocalStorage<T>(key: string, initialValue: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initialValue;
    } catch (err) {
      logger.warn("[local-storage] failed to read", key, err);
      return initialValue;
    }
  });

  const setStoredValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = next instanceof Function ? next(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch (err) {
          logger.warn("[local-storage] failed to write", key, err);
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setStoredValue] as const;
}
